import { useMemo, useRef, useState } from "react";
import {
  canUpdateLinkedRow,
  createScheduleTaggingService,
  getScheduleDocumentType,
  getLinkedRowUpdateKey,
} from "../services/scheduleTaggingService";

export function useScheduleTaggingActions({
  tcId,
  companyType,
  addAutoloadData,
  deleteAutoloadData,
  createPLSchedule,
  createCASchedule,
  updateLinkedAutoloadData,
  onSuccess,
  onSchedulesChanged,
  onError,
}) {
  const pendingUpdatesRef = useRef(new Map());
  const [updatingRowKeys, setUpdatingRowKeys] = useState([]);
  const service = useMemo(
    () => createScheduleTaggingService({
      tcId,
      companyType,
      addAutoloadData,
      deleteAutoloadData,
      createPLSchedule,
      createCASchedule,
      updateLinkedAutoloadData,
    }),
    [tcId, companyType, addAutoloadData, deleteAutoloadData, createPLSchedule, createCASchedule, updateLinkedAutoloadData]
  );

  return useMemo(() => {
    const runLinkedUpdate = (sources, execute, showMessage, successMessage) => {
      const keys = (sources || [])
        .map((source) => getLinkedRowUpdateKey(source, tcId))
        .filter(Boolean);
      const existing = keys
        .map((key) => pendingUpdatesRef.current.get(key))
        .find(Boolean);
      if (existing) return existing;

      const request = Promise.resolve()
        .then(execute)
        .then((response) => {
          if (showMessage) onSuccess?.(successMessage);
          return response;
        })
        .catch((error) => {
          onError?.(error, error?.message || "Unable to update the linked schedule.");
          throw error;
        })
        .finally(() => {
          keys.forEach((key) => pendingUpdatesRef.current.delete(key));
          setUpdatingRowKeys([...pendingUpdatesRef.current.keys()]);
        });

      keys.forEach((key) => pendingUpdatesRef.current.set(key, request));
      setUpdatingRowKeys([...pendingUpdatesRef.current.keys()]);
      return request;
    };

    return {
      ...service,
      isLinkedRowUpdating(source) {
        return updatingRowKeys.includes(getLinkedRowUpdateKey(source, tcId));
      },
      // Save-time confirmation can reuse this action with { showMessage: false }.
      // Keep error propagation so callers can decide whether a save may continue.
      updateLinkedRow(source, { showMessage = true } = {}) {
        return runLinkedUpdate(
          [source],
          () => service.updateLinkedRow(source),
          showMessage,
          "Linked schedule updated successfully."
        );
      },
      updateLinkedRows(sources, { showMessage = true } = {}) {
        const valid = (sources || []).filter((source) => canUpdateLinkedRow(source));
        const count = valid.length;
        return runLinkedUpdate(
          valid,
          () => service.updateLinkedRows(valid),
          showMessage,
          count === 1
            ? "Linked schedule updated successfully."
            : `Linked schedules updated for ${count} rows.`
        );
      },
      async unTagSchedule(sourceType, rowId, targetDocType, showMessage = true) {
        const response = await service.unTagSchedule(sourceType, rowId, targetDocType);
        if (response && showMessage) {
          onSuccess?.("Successfully removed the adjustment.");
        }
        return response;
      },
      async createTagSchedule(data, reference) {
        try {
          return await service.createTagSchedule(data, reference);
        } finally {
          // Refresh independently: a list-fetch failure must not turn a
          // completed link into an apparent tagging failure.
          const family =
            getScheduleDocumentType(reference.scheduleType) === "ca_schedule"
              ? "CA"
              : "PL";
          Promise.resolve()
            .then(() => onSchedulesChanged?.(family, tcId))
            .catch((error) => onError?.(error));
        }
      },
    };
  }, [
    tcId,
    service,
    updatingRowKeys,
    onSuccess,
    onSchedulesChanged,
    onError,
  ]);
}
